import { useEffect, useRef, useState } from 'react'

import { ImagePreviewApprovalDialog } from '@/components/image-generation/image-preview-approval-dialog'
import { ImagePromptDialog } from '@/components/image-generation/image-prompt-dialog'
import type { ConfirmImagePreviewResult } from '@/features/image-generation'
import { useProjectImageStore } from '@/stores/project-image-store'

type ImageGenerationWorkflowDialogsProps = {
  onApproved?: (result: ConfirmImagePreviewResult) => Promise<void> | void
}

export function ImageGenerationWorkflowDialogs({
  onApproved,
}: ImageGenerationWorkflowDialogsProps) {
  const approvePreview = useProjectImageStore((state) => state.approvePreview)
  const cancelDraftSession = useProjectImageStore((state) => state.cancelDraftSession)
  const draftSession = useProjectImageStore((state) => state.draftSession)
  const generatePreview = useProjectImageStore((state) => state.generatePreview)
  const rejectPreview = useProjectImageStore((state) => state.rejectPreview)
  const updateDraftPrompt = useProjectImageStore((state) => state.updateDraftPrompt)
  const [approving, setApproving] = useState(false)
  const openerRef = useRef<HTMLElement | null>(null)
  const budgetRestricted = draftSession
    ? draftSession.accessState.code === 'budget-restricted'
    : false
  const promptBlocked = draftSession
    ? draftSession.accessState.code !== 'available'
    : false
  const showPromptDialog = Boolean(
    draftSession
    && (!draftSession.previewDataUrl || promptBlocked || budgetRestricted),
  )

  useEffect(() => {
    if (!draftSession || openerRef.current) {
      return
    }

    if (document.activeElement instanceof HTMLElement) {
      openerRef.current = document.activeElement
    }
  }, [draftSession])

  function restoreFocus() {
    const opener = openerRef.current
    openerRef.current = null

    if (!opener) {
      return
    }

    window.requestAnimationFrame(() => {
      opener.focus()
    })
  }

  async function handleClose() {
    const result = draftSession?.tempAssetRef
      ? await rejectPreview()
      : (cancelDraftSession(), null)

    if (draftSession?.tempAssetRef && !result) {
      return
    }

    restoreFocus()
  }

  if (!draftSession) {
    return null
  }

  async function handleApprove() {
    setApproving(true)

    try {
      const result = await approvePreview()
      if (!result) {
        return
      }

      await onApproved?.(result)
      restoreFocus()
    } finally {
      setApproving(false)
    }
  }

  return (
    <>
      <ImagePromptDialog
        onGenerate={() => void generatePreview()}
        onOpenChange={(open) => {
          if (!open) {
            void handleClose()
          }
        }}
        onPromptChange={updateDraftPrompt}
        open={showPromptDialog}
        session={draftSession}
      />
      <ImagePreviewApprovalDialog
        approving={approving}
        onApprove={() => void handleApprove()}
        onOpenChange={(open) => {
          if (!open) {
            void handleClose()
          }
        }}
        onRegenerate={() => void generatePreview()}
        onReject={() => void rejectPreview()}
        open={Boolean(draftSession.previewDataUrl) && !promptBlocked}
        session={draftSession}
      />
    </>
  )
}
